import { GameGuide } from '../types/api'

export const gameGuides: GameGuide[] = [
  {
    title: "Getting Started in Grow a Garden",
    content: `
      <h3>Welcome to Grow a Garden!</h3>
      <p>Grow a Garden is a farming simulation game where you plant seeds, harvest crops, and build your garden empire.</p>
      
      <h4>Basic Mechanics:</h4>
      <ul>
        <li><strong>Planting:</strong> Use seeds to plant crops in your garden plots</li>
        <li><strong>Weather:</strong> Different weather conditions affect crop growth and yield</li>
        <li><strong>Harvesting:</strong> Collect mature crops for profit and resources</li>
        <li><strong>Trading:</strong> Buy and sell items with the traveling merchant</li>
      </ul>
      
      <h4>First Steps:</h4>
      <ol>
        <li>Start with common seeds to learn the basics</li>
        <li>Check weather conditions before planting</li>
        <li>Save money for rare items and gear</li>
        <li>Participate in events for exclusive rewards</li>
      </ol>
    `,
    category: 'beginner',
    tags: ['basics', 'getting-started', 'mechanics'],
    last_updated: Date.now()
  },
  {
    title: "Weather System Guide",
    content: `
      <h3>Understanding Weather in Grow a Garden</h3>
      <p>Weather plays a crucial role in your farming success. Different weather conditions provide various bonuses and challenges.</p>
      
      <h4>Weather Types and Effects:</h4>
      <ul>
        <li><strong>Sunny:</strong> +20% crop growth speed, +10% yield</li>
        <li><strong>Rainy:</strong> +30% crop growth speed, +15% yield, reduced water costs</li>
        <li><strong>Stormy:</strong> +50% crop growth speed, +25% yield, but crops can be damaged</li>
        <li><strong>Cloudy:</strong> Normal growth, +5% yield</li>
        <li><strong>Windy:</strong> +15% crop growth speed, but increased chance of crop damage</li>
        <li><strong>Snowy:</strong> Slower growth but +40% yield for winter crops</li>
      </ul>
      
      <h4>Weather Strategy:</h4>
      <ul>
        <li>Plant fast-growing crops during stormy weather</li>
        <li>Use protective gear during windy conditions</li>
        <li>Save rare seeds for optimal weather conditions</li>
        <li>Monitor weather forecasts to plan your planting schedule</li>
      </ul>
    `,
    category: 'advanced',
    tags: ['weather', 'strategy', 'crops'],
    last_updated: Date.now()
  },
  {
    title: "Rarity System Explained",
    content: `
      <h3>Item Rarity in Grow a Garden</h3>
      <p>Items in Grow a Garden come in different rarity levels, each with unique properties and values.</p>
      
      <h4>Rarity Levels:</h4>
      <ul>
        <li><strong>Common (Gray):</strong> Basic items, easy to obtain, low value</li>
        <li><strong>Uncommon (Green):</strong> Better than common, moderate value</li>
        <li><strong>Rare (Blue):</strong> Harder to find, high value, special effects</li>
        <li><strong>Epic (Purple):</strong> Very rare, very high value, unique abilities</li>
        <li><strong>Legendary (Orange):</strong> Extremely rare, extremely valuable</li>
        <li><strong>Mythic (Red):</strong> The rarest items, highest value, game-changing effects</li>
      </ul>
      
      <h4>Rarity Strategy:</h4>
      <ul>
        <li>Focus on rare and epic items for maximum profit</li>
        <li>Legendary and mythic items are investment pieces</li>
        <li>Common items are good for beginners and daily farming</li>
        <li>Rarity often correlates with crop yield and growth speed</li>
      </ul>
    `,
    category: 'tips',
    tags: ['rarity', 'items', 'value'],
    last_updated: Date.now()
  },
  {
    title: "Traveling Merchant Guide",
    content: `
      <h3>Mastering the Traveling Merchant</h3>
      <p>The Traveling Merchant appears periodically with unique items not available in regular shops.</p>
      
      <h4>Merchant Schedule:</h4>
      <ul>
        <li>Appears every 4-6 hours</li>
        <li>Stays for 2-3 hours</li>
        <li>Brings 5-10 unique items</li>
        <li>Items are often rare or exclusive</li>
      </ul>
      
      <h4>Merchant Strategy:</h4>
      <ul>
        <li>Always check merchant inventory when available</li>
        <li>Prioritize rare and epic items</li>
        <li>Save money specifically for merchant visits</li>
        <li>Some merchant items are event-exclusive</li>
        <li>Merchant items often have better value than regular shop items</li>
      </ul>
      
      <h4>Best Practices:</h4>
      <ul>
        <li>Set notifications for merchant arrivals</li>
        <li>Research item values before purchasing</li>
        <li>Don't buy everything - focus on high-value items</li>
        <li>Consider reselling merchant items for profit</li>
      </ul>
    `,
    category: 'advanced',
    tags: ['merchant', 'trading', 'exclusive'],
    last_updated: Date.now()
  },
  {
    title: "Event Participation Guide",
    content: `
      <h3>Making the Most of Events</h3>
      <p>Events in Grow a Garden provide unique opportunities for rare items and exclusive rewards.</p>
      
      <h4>Event Types:</h4>
      <ul>
        <li><strong>Seasonal Events:</strong> Limited-time themes with special items</li>
        <li><strong>Holiday Events:</strong> Special celebrations with exclusive rewards</li>
        <li><strong>Community Events:</strong> Collaborative challenges with shared rewards</li>
        <li><strong>Competitive Events:</strong> Leaderboard-based competitions</li>
      </ul>
      
      <h4>Event Strategy:</h4>
      <ul>
        <li>Participate in all events for maximum rewards</li>
        <li>Focus on event-exclusive items and cosmetics</li>
        <li>Events often have better item drop rates</li>
        <li>Save resources for major events</li>
        <li>Collaborate with other players during community events</li>
      </ul>
      
      <h4>Event Preparation:</h4>
      <ul>
        <li>Stock up on resources before events</li>
        <li>Clear garden space for event crops</li>
        <li>Have backup seeds and gear ready</li>
        <li>Monitor event schedules and requirements</li>
      </ul>
    `,
    category: 'events',
    tags: ['events', 'rewards', 'exclusive'],
    last_updated: Date.now()
  },
  {
    title: "Advanced Farming Techniques",
    content: `
      <h3>Advanced Strategies for Experienced Players</h3>
      <p>Once you've mastered the basics, these advanced techniques will help you maximize your profits and efficiency.</p>
      
      <h4>Crop Rotation:</h4>
      <ul>
        <li>Plant different crop types to maintain soil health</li>
        <li>Some crops improve growth conditions for others</li>
        <li>Rotate between fast and slow-growing crops</li>
        <li>Use crop rotation to optimize space usage</li>
      </ul>
      
      <h4>Weather Optimization:</h4>
      <ul>
        <li>Plant weather-sensitive crops during optimal conditions</li>
        <li>Use protective gear during adverse weather</li>
        <li>Time your harvests with weather bonuses</li>
        <li>Monitor weather patterns for long-term planning</li>
      </ul>
      
      <h4>Market Timing:</h4>
      <ul>
        <li>Buy low during oversupply periods</li>
        <li>Sell high when demand is strong</li>
        <li>Hold rare items for price appreciation</li>
        <li>Diversify your inventory across different categories</li>
      </ul>
      
      <h4>Resource Management:</h4>
      <ul>
        <li>Maintain a balanced inventory of seeds and gear</li>
        <li>Keep emergency funds for rare opportunities</li>
        <li>Invest in quality tools for better efficiency</li>
        <li>Plan your purchases around merchant visits</li>
      </ul>
    `,
    category: 'advanced',
    tags: ['strategy', 'optimization', 'profit'],
    last_updated: Date.now()
  },
  {
    title: "Fruit Calculation Mastery",
    content: `
      <h3>Understanding Fruit Value Calculations</h3>
      <p>Fruit values in Grow a Garden are determined by multiple factors including weight, variants, and mutations.</p>
      
      <h4>Value Factors:</h4>
      <ul>
        <li><strong>Weight:</strong> Heavier fruits are generally more valuable</li>
        <li><strong>Variants:</strong> Special variants can multiply value significantly</li>
        <li><strong>Mutations:</strong> Rare mutations create the most valuable fruits</li>
        <li><strong>Rarity:</strong> Rarer base fruits have higher value potential</li>
      </ul>
      
      <h4>Optimal Growing Conditions:</h4>
      <ul>
        <li>Use high-quality seeds for better variants</li>
        <li>Plant during optimal weather for mutations</li>
        <li>Use fertilizer and gear to increase weight</li>
        <li>Harvest at peak ripeness for maximum value</li>
      </ul>
      
      <h4>Value Maximization:</h4>
      <ul>
        <li>Focus on fruits with high base values</li>
        <li>Experiment with different growing conditions</li>
        <li>Save rare variants for special events</li>
        <li>Track which conditions produce the best results</li>
      </ul>
    `,
    category: 'advanced',
    tags: ['fruit', 'value', 'calculation'],
    last_updated: Date.now()
  }
]

export const quickTips = [
  "Check weather before planting - it affects crop growth and yield!",
  "Save money for the Traveling Merchant - they have exclusive items!",
  "Rare and epic items are better investments than common ones",
  "Participate in events for exclusive rewards and items",
  "Use crop rotation to maintain soil health and optimize space",
  "Monitor market trends to buy low and sell high",
  "Legendary and mythic items are long-term investments",
  "Stormy weather gives the biggest growth bonuses but risks crop damage",
  "Always have backup seeds and gear ready for events",
  "Collaborate with other players during community events"
]

export const weatherEffects = {
  sunny: { growth: 1.2, yield: 1.1, description: "Perfect for most crops" },
  rainy: { growth: 1.3, yield: 1.15, description: "Great for water-loving plants" },
  stormy: { growth: 1.5, yield: 1.25, description: "Maximum growth but risky" },
  cloudy: { growth: 1.0, yield: 1.05, description: "Safe and steady growth" },
  windy: { growth: 1.15, yield: 1.0, description: "Fast growth with some risk" },
  snowy: { growth: 0.8, yield: 1.4, description: "Slow but high yield for winter crops" }
}

export const rarityValues = {
  common: { baseValue: 1, dropRate: 0.6, description: "Basic items, easy to obtain" },
  uncommon: { baseValue: 2, dropRate: 0.25, description: "Better than common, moderate value" },
  rare: { baseValue: 5, dropRate: 0.1, description: "Harder to find, high value" },
  epic: { baseValue: 15, dropRate: 0.04, description: "Very rare, very high value" },
  legendary: { baseValue: 50, dropRate: 0.008, description: "Extremely rare, extremely valuable" },
  mythic: { baseValue: 200, dropRate: 0.002, description: "The rarest items, highest value" },
  divine: { baseValue: 500, dropRate: 0.0005, description: "Divine items, beyond mortal comprehension" },
  mythical: { baseValue: 1000, dropRate: 0.0001, description: "Mythical items, the stuff of legends" },
  prismatic: { baseValue: 2500, dropRate: 0.00001, description: "Prismatic items, the rarest of the rare, beyond reality" }
} 